import { useEffect, useState, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import * as Icons from 'lucide-react'
import {
  Users, Plus, Edit3, Trash2, Crown, X, UserPlus, Search, Upload, Link as LinkIcon,
  Shield, LogOut, ExternalLink,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import PageHeader from '@/components/PageHeader'
import IconPicker from '@/components/IconPicker'
import { teamsAPI, playersAPI } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'

/* Persistente teams — één keer aanmaken met vaste naam en volledige leden-
   lijst. Steam-achtige team-page: hero banner met kleur/logo, ledenlijst
   als vriendenlijst. Team wordt beheerd door zijn kapiteins en admins. */

const KLEUR_KEUZE = [
  '#B4FF39', '#61E294', '#3adf3a', '#00d9ff', '#337cff',
  '#8b3fff', '#c77bff', '#ff2b8f', '#ff5a5a', '#ff9500',
  '#ffcf40', '#E4C265', '#ffffff', '#94a3b8',
]

function icoonVan(naam) { return Icons[naam] || Users }

export default function TeamsPage() {
  const { user: currentUser, isAdmin } = useAuth()
  const [teams, setTeams]         = useState([])
  const [teamFilter, setTeamFilter] = useState('all') // 'all' of 'mine'
  const [selectedId, setSelected] = useState(null)
  const [detail, setDetail]       = useState(null)   // { team, members }
  const [dlg, setDlg]             = useState(null)   // team-bewerk-dialoog
  const [addDlg, setAddDlg]       = useState(false)  // lid-toevoeg-dialoog
  const [zoekQ, setZoekQ]         = useState('')
  const [zoekLijst, setZoekLijst] = useState([])
  const [error, setError]         = useState(null)
  const [myTeamIds, setMyTeamIds] = useState(new Set())

  const laadTeams = useCallback(() => {
    // /teams geeft alle teams (met members_count); /teams/mine geeft alleen
    // waar ik in zit. We hebben beide nodig — de "Mijn teams"-filter checkte
    // vroeger t.members / t.captain_user_id die de /teams-lijst niet bevat.
    Promise.all([teamsAPI.list(), teamsAPI.mine()])
      .then(([all, mine]) => {
        setTeams(all.data?.teams ?? [])
        setMyTeamIds(new Set((mine.data?.teams ?? []).map(t => t.id)))
      })
      .catch(e => setError(e.response?.data?.error ?? e.message))
  }, [])

  const laadDetail = useCallback((id) => {
    if (!id) { setDetail(null); return }
    teamsAPI.get(id).then(r => setDetail(r.data)).catch(e => setError(e.response?.data?.error ?? e.message))
  }, [])

  useEffect(() => { laadTeams() }, [laadTeams])
  useEffect(() => { laadDetail(selectedId) }, [selectedId, laadDetail])

  // Live user-search in add-member dialoog.
  useEffect(() => {
    if (!addDlg) return
    const t = setTimeout(() => {
      playersAPI.list(zoekQ).then(r => setZoekLijst(r.data?.players ?? r.data?.users ?? [])).catch(() => setZoekLijst([]))
    }, 200)
    return () => clearTimeout(t)
  }, [zoekQ, addDlg])

  // Ben ik kapitein/admin van het geselecteerde team?
  const kanBeheren = (() => {
    if (!detail) return false
    if (isAdmin) return true
    return detail.members.some(m => m.user_id === currentUser?.id && m.role === 'captain')
  })()
  const benLid = detail?.members?.some(m => m.user_id === currentUser?.id)

  const bewaar = async (e) => {
    e.preventDefault()
    setError(null)
    const body = {
      name: dlg.name, description: dlg.description,
      logo_url: dlg.logo_url,
      primary_color: dlg.primary_color, secondary_color: dlg.secondary_color,
      icon_name: dlg.icon_name,
    }
    try {
      if (dlg.id) await teamsAPI.update(dlg.id, body)
      else {
        const r = await teamsAPI.create(body)
        setSelected(r.data?.id)
      }
      setDlg(null); laadTeams(); if (dlg.id) laadDetail(dlg.id)
    } catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const wis = async (id) => {
    if (!window.confirm('Dit team verwijderen? Alle koppelingen naar leden gaan mee.')) return
    try { await teamsAPI.remove(id); setSelected(null); laadTeams() }
    catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const voegLidToe = async (userId) => {
    // Invite-flow: notification naar de uitgenodigde user. Die accepteert
    // of weigert zelf — nooit unilateraal door de kapitein.
    try {
      await teamsAPI.invite(selectedId, userId)
      setAddDlg(false); setZoekQ(''); setZoekLijst([])
      laadDetail(selectedId); laadTeams()
    } catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const wisLid = async (userId) => {
    try { await teamsAPI.removeMember(selectedId, userId); laadDetail(selectedId); laadTeams() }
    catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const wisselKapitein = async (userId, huidig) => {
    const nieuw = huidig === 'captain' ? 'member' : 'captain'
    try { await teamsAPI.setRole(selectedId, userId, nieuw); laadDetail(selectedId) }
    catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  return (
    <div>
      <PageHeader
        icon={Users}
        title="Teams"
        subtitle="Vaste teams beheren die op meerdere compo's kunnen deelnemen"
      />

      {error && <p className="text-sm text-signal mb-3">{error}</p>}

      <div className="grid gap-4 lg:grid-cols-[22rem_1fr]">
        {/* Teams-lijst links — filter tussen alle teams en de mijne. */}
        <TeamLijst
          teams={teams.filter(t => teamFilter === 'all' ? true : myTeamIds.has(t.id))}
          filter={teamFilter}
          onFilterChange={setTeamFilter}
          selectedId={selectedId}
          onSelect={setSelected}
          onNew={() => setDlg({ name: '', description: '', logo_url: '',
                                primary_color: '#B4FF39', secondary_color: '',
                                icon_name: 'Users' })}
        />

        {/* Details rechts */}
        {!detail ? (
          <Card>
            <CardContent className="p-12 text-center text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Selecteer een team links om z'n leden te beheren, of maak een nieuw team aan.</p>
            </CardContent>
          </Card>
        ) : (
          <TeamProfiel
            team={detail.team} members={detail.members}
            currentUserId={currentUser?.id}
            kanBeheren={kanBeheren}
            benLid={benLid}
            onEdit={() => setDlg({
              id: detail.team.id,
              name: detail.team.name, description: detail.team.description ?? '',
              logo_url: detail.team.logo_url ?? '',
              primary_color: detail.team.primary_color ?? '#B4FF39',
              secondary_color: detail.team.secondary_color ?? '',
              icon_name: detail.team.icon_name ?? 'Users',
            })}
            onDelete={() => wis(detail.team.id)}
            onAdd={() => { setAddDlg(true); setZoekQ('') }}
            onRemoveMember={wisLid}
            onToggleCaptain={wisselKapitein}
            onLogoUploaded={() => laadDetail(selectedId)}
          />
        )}
      </div>

      {dlg && (
        <TeamDialog
          dlg={dlg} setDlg={setDlg} onSubmit={bewaar}
        />
      )}

      {addDlg && (
        <Dialog open onOpenChange={(o) => !o && setAddDlg(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Uitnodigen voor {detail?.team?.name}</DialogTitle>
              <p className="text-xs text-muted-foreground">
                De uitgenodigde speler krijgt een notificatie en beslist zelf om te accepteren.
              </p>
            </DialogHeader>
            <div className="space-y-3">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={zoekQ} onChange={e => setZoekQ(e.target.value)} autoFocus
                       placeholder="Zoek user…" className="pl-8" />
              </div>
              <div className="max-h-[24rem] overflow-y-auto space-y-1">
                {zoekLijst.length === 0 && (
                  <p className="text-sm text-muted-foreground p-2">Geen gebruiker gevonden.</p>
                )}
                {zoekLijst
                  .filter(u => !detail?.members?.some(m => m.user_id === u.id))
                  .slice(0, 30)
                  .map(u => (
                    <button key={u.id} type="button" onClick={() => voegLidToe(u.id)}
                            className="w-full flex items-center gap-3 rounded-[4px] border border-border/40 bg-card px-3 py-2 text-left hover:border-acid/60 transition-colors">
                      <Avatar naam={u.display_name || u.username} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{u.display_name || u.username}</div>
                        <div className="font-mono text-[10px] text-muted-foreground truncate">@{u.username}</div>
                      </div>
                    </button>
                  ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setAddDlg(false)}>Sluit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

/* ── Sub-componenten ─────────────────────────────────────── */

function Avatar({ naam, size = 8 }) {
  const initialen = (naam || '?').trim().slice(0, 2).toUpperCase()
  return (
    <div className={`h-${size} w-${size} rounded-full bg-muted grid place-items-center shrink-0 font-semibold text-xs`}>
      {initialen}
    </div>
  )
}

function TeamLijst({ teams, selectedId, onSelect, onNew, filter, onFilterChange }) {
  return (
    <Card>
      <CardContent className="p-3 space-y-1.5">
        <div className="flex items-center justify-between mb-2 px-1 gap-2">
          <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
            Teams · {teams.length}
          </div>
          <Button size="sm" onClick={onNew}>
            <Plus className="h-4 w-4 mr-1" /> Nieuw
          </Button>
        </div>
        {onFilterChange && (
          <div className="flex gap-1 px-1 mb-2">
            {[{k:'all',l:'Alle'},{k:'mine',l:'Mijn teams'}].map(o => (
              <button key={o.k} type="button"
                      onClick={() => onFilterChange(o.k)}
                      className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest rounded border transition-colors ${
                        filter === o.k
                          ? 'border-acid bg-acid/10 text-acid'
                          : 'border-border text-muted-foreground hover:border-acid/40'
                      }`}>
                {o.l}
              </button>
            ))}
          </div>
        )}
        {teams.length === 0 && (
          <p className="text-sm text-muted-foreground px-1 py-2">Nog geen teams. Maak er een aan.</p>
        )}
        {teams.map(t => {
          const Icon = icoonVan(t.icon_name)
          const actief = selectedId === t.id
          return (
            <button key={t.id} onClick={() => onSelect(t.id)}
                    className={`w-full flex items-center gap-3 rounded-[4px] border px-3 py-2 text-left transition-colors ${
                      actief ? 'border-acid bg-acid/10' : 'border-border hover:border-acid/40'
                    }`}
                    style={{ borderLeft: `3px solid ${t.primary_color}` }}>
              {t.logo_url
                ? <img src={t.logo_url} alt="" className="h-9 w-9 rounded-[4px] object-cover shrink-0" />
                : <div className="h-9 w-9 rounded-[4px] grid place-items-center shrink-0"
                       style={{ background: `${t.primary_color}22` }}>
                    <Icon className="h-4 w-4" style={{ color: t.primary_color }} />
                  </div>}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{t.name}</div>
                <div className="font-mono text-[10px] text-muted-foreground">
                  {t.members_count} {t.members_count === 1 ? 'lid' : 'leden'}
                </div>
              </div>
            </button>
          )
        })}
      </CardContent>
    </Card>
  )
}

function TeamProfiel({
  team, members, currentUserId, kanBeheren, benLid,
  onEdit, onDelete, onAdd, onRemoveMember, onToggleCaptain, onLogoUploaded,
}) {
  const Icon = icoonVan(team.icon_name)
  const kleur = team.primary_color || '#B4FF39'
  const kleur2 = team.secondary_color || kleur
  const fileRef = useRef()

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    try { await teamsAPI.uploadLogo(team.id, file); onLogoUploaded?.() }
    catch (err) { window.alert(err.response?.data?.error ?? err.message) }
    finally { if (fileRef.current) fileRef.current.value = '' }
  }

  return (
    <Card className="overflow-hidden" style={{ borderTop: `3px solid ${kleur}` }}>
      {/* Hero — kleur-gradient + logo/icon groot */}
      <div className="relative p-6 pb-4"
           style={{ background: `linear-gradient(135deg, ${kleur}18 0%, ${kleur2}08 60%, transparent 100%)` }}>
        <div className="flex items-start gap-5 flex-wrap">
          <div className="relative">
            {team.logo_url ? (
              <img src={team.logo_url} alt=""
                   className="h-24 w-24 rounded-[8px] object-cover border-2 shrink-0"
                   style={{ borderColor: kleur }} />
            ) : (
              <div className="h-24 w-24 rounded-[8px] grid place-items-center border-2 shrink-0"
                   style={{ borderColor: kleur, background: `${kleur}18` }}>
                <Icon className="h-12 w-12" style={{ color: kleur }} />
              </div>
            )}
            {kanBeheren && (
              <>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
                <button type="button" onClick={() => fileRef.current?.click()}
                        className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-card border-2 border-border grid place-items-center hover:bg-acid/10 hover:border-acid transition-colors"
                        title="Nieuw logo uploaden">
                  <Upload className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </div>

          <div className="flex-1 min-w-[14rem]">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-1">
              Team
            </div>
            <h2 className="text-3xl font-bold tracking-tight leading-none m-0"
                style={{ color: kleur }}>{team.name}</h2>
            {team.description && (
              <p className="text-sm text-muted-foreground mt-2 max-w-[50ch] leading-relaxed">
                {team.description}
              </p>
            )}
            <div className="flex gap-4 mt-3 flex-wrap font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              <span>{members.length} {members.length === 1 ? 'lid' : 'leden'}</span>
              <span>· {members.filter(m => m.role === 'captain').length} kapitein(s)</span>
              <span>· aangemaakt {new Date(team.created_at).toLocaleDateString('nl-BE', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button asChild variant="outline" size="sm">
              <Link to={`/teams/${team.id}`}><ExternalLink className="h-4 w-4 mr-1" /> Bekijk profiel</Link>
            </Button>
            {kanBeheren && <Button variant="outline" size="sm" onClick={onEdit}>
              <Edit3 className="h-4 w-4 mr-1" /> Bewerken
            </Button>}
            {kanBeheren && <Button variant="outline" size="sm" onClick={onDelete}>
              <Trash2 className="h-4 w-4 text-signal" />
            </Button>}
            {benLid && !kanBeheren && (
              <Button variant="outline" size="sm" onClick={() => onRemoveMember(currentUserId)}>
                <LogOut className="h-4 w-4 mr-1" /> Verlaat team
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Members — vriendenlijst-stijl */}
      <div className="p-6 pt-4 border-t border-border/40">
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            Leden
          </div>
          {kanBeheren && (
            <Button size="sm" onClick={onAdd}>
              <UserPlus className="h-4 w-4 mr-1" /> Speler uitnodigen
            </Button>
          )}
        </div>

        {members.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nog geen leden. Voeg er een toe.</p>
        ) : (
          <div className="grid gap-1.5 sm:grid-cols-2">
            {members.map(m => (
              <div key={m.membership_id}
                   className="flex items-center gap-3 rounded-[4px] border bg-card/40 px-3 py-2 relative"
                   style={{ borderColor: m.role === 'captain' ? `${kleur}66` : 'var(--border)' }}>
                <Avatar naam={m.display_name || m.username} size={9} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                    {m.display_name || m.username}
                    {m.role === 'captain' && (
                      <span className="inline-flex items-center gap-0.5 font-mono text-[9px] uppercase tracking-widest"
                            style={{ color: kleur }}>
                        <Crown className="h-3 w-3" /> kapitein
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground truncate">@{m.username}</div>
                </div>
                {kanBeheren && (
                  <>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0"
                            onClick={() => onToggleCaptain(m.user_id, m.role)}
                            title={m.role === 'captain' ? 'Kapitein-rol wegnemen' : 'Maak kapitein'}>
                      <Crown className={`h-3.5 w-3.5 ${m.role === 'captain' ? 'text-gold' : 'text-muted-foreground'}`} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0"
                            onClick={() => onRemoveMember(m.user_id)}
                            title="Uit team halen">
                      <X className="h-4 w-4 text-signal" />
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {!benLid && !kanBeheren && (
          <p className="text-xs text-muted-foreground mt-4">
            Alleen kapiteins van dit team of admins kunnen leden toevoegen. Vraag een kapitein om je toe te voegen.
          </p>
        )}
      </div>
    </Card>
  )
}

function TeamDialog({ dlg, setDlg, onSubmit }) {
  return (
    <Dialog open onOpenChange={(o) => !o && setDlg(null)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{dlg.id ? 'Team bewerken' : 'Nieuw team'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <Label>Naam *</Label>
            <Input value={dlg.name} required
                   onChange={e => setDlg({ ...dlg, name: e.target.value })}
                   placeholder="666" />
          </div>
          <div>
            <Label>Omschrijving</Label>
            <Input value={dlg.description}
                   onChange={e => setDlg({ ...dlg, description: e.target.value })}
                   placeholder="Vaste crew, gaat mee op elk toernooi en show" />
          </div>
          <div>
            <Label>Icoon</Label>
            <IconPicker value={dlg.icon_name}
                        onChange={v => setDlg({ ...dlg, icon_name: v })}
                        kleur={dlg.primary_color} />
          </div>
          <div>
            <Label className="flex items-center gap-1.5">
              <LinkIcon className="h-3.5 w-3.5" /> Logo URL (optioneel — of upload later op de team-pagina)
            </Label>
            <Input value={dlg.logo_url}
                   onChange={e => setDlg({ ...dlg, logo_url: e.target.value })}
                   placeholder="https://…/logo.png" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <KleurVeld label="Hoofdkleur"
                       value={dlg.primary_color}
                       onChange={v => setDlg({ ...dlg, primary_color: v })} />
            <KleurVeld label="Bijkleur (optioneel)"
                       value={dlg.secondary_color}
                       onChange={v => setDlg({ ...dlg, secondary_color: v })} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setDlg(null)}>Annuleer</Button>
            <Button type="submit">{dlg.id ? 'Opslaan' : 'Aanmaken'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function KleurVeld({ label, value, onChange }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <input type="color" value={value || '#94a3b8'}
               onChange={e => onChange(e.target.value)}
               className="h-9 w-12 rounded-[4px] border border-border cursor-pointer bg-transparent" />
        <Input value={value ?? ''} onChange={e => onChange(e.target.value)}
               className="flex-1 font-mono text-xs" placeholder="#RRGGBB" />
      </div>
      <div className="flex gap-1 flex-wrap mt-1.5">
        {KLEUR_KEUZE.map(c => (
          <button key={c} type="button" onClick={() => onChange(c)}
                  className="h-5 w-5 rounded-[3px] border border-border"
                  style={{ background: c,
                           outline: (value ?? '').toLowerCase() === c.toLowerCase() ? '2px solid var(--acid, #B4FF39)' : 'none',
                           outlineOffset: '1px' }} />
        ))}
      </div>
    </div>
  )
}
